// App.js
import React from 'react';
import { HashRouter as Router, Route, Routes } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Landing from './pages/Landing';
import Signin from './pages/Signin';
import Signup from './pages/SignUp';
import Dashboard from './pages/Dashboard';
import CreateNewOrder from './pages/order/CreateNewOrder';
import CurrentOrder from './pages/order/CurrentOrder';
import OrderDetail from './pages/order/OrderDetail.js';
import OrderDetailFinished from './pages/order/OrderDetailFinished.js';
import OrderDetailDNP from './pages/order/OrderDetailDNP.js';
import FinishedOrder from './pages/order/FinishedOrder';
import DoNotProceed from './pages/order/DoNotProceed';
import RecentlyDeleted from './pages/order/RecentlyDeleted';
import OrderDetailDeleted from './pages/order/OrderDetailDeleted';
import QuoteCalculator from './pages/tools/QuoteCalculator';

// Organization Settings
import OrganizationSettings from './pages/organization/OrganizationSettings';
import CreateOrganization from './pages/organization/CreateOrganization';
import OrgSettings from './pages/organization/OrgSettings';
import CreateJobFlow from './pages/organization/CreateJobFlow';
import JobFlowSettings from './pages/organization/JobFlowSettings';
import EditJobFlowSettings from './pages/organization/EditJobFlowSettings';

// Organization Roles
import OrganizationRoles from './pages/organizationRoles/OrganizationRoles';
import EditOrganizationRoles from './pages/organizationRoles/EditOrganizationRoles';
import CreateOrganizationRoles from './pages/organizationRoles/CreateOrganizationRoles';

// Organization Members
import OrganizationMembers from './pages/organizationMembers/OrganizationMembers';
import CreateOrganizationMember from './pages/organizationMembers/CreateOrganizationMember';
import EditOrganizationMember from './pages/organizationMembers/EditOrganizationMember';

// Product Management
import ProductManagement from './pages/productManagement/ProductManagement';

// Customer Management
import CustomerManagement from './pages/customerManagement/CustomerManagement';
import CreateCustomer from './pages/customerManagement/CreateCustomer';
import EditCustomer from './pages/customerManagement/EditCustomer';
import EditCompany from './pages/customerManagement/EditCompany';

import SystemSettings from './pages/settings/SystemSettings';
import Profile from './pages/Profile';

import Chat from './pages/Chat';

import AdminSignin from './pages/AdminSignin';
import AdminDashboard from './pages/admins/AdminDashboard';
import AdminAdmins from './pages/admins/AdminAdmins';
import AdminOrganizations from './pages/admins/AdminOrganizations';
import AdminMembers from './pages/admins/AdminMembers';
import AdminSessions from './pages/admins/AdminSessions';
import AdminEvents from './pages/admins/AdminEvents';
import AdminSettings from './pages/admins/AdminSettings';
import AdminOrganizationDetails from './pages/admins/AdminOrganizationDetails.js';

import { WebSocketProvider } from './pages/ws/WebsocketContext';

import NotFound from './pages/NotFound';
import Unauthorized from './pages/Unauthorized.js';

const App = () => {
  return (
    <WebSocketProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/signin" element={<Signin />} />
          <Route path="/signup" element={<Signup />} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/order/create-order/:orderId" element={
            <ProtectedRoute>
              <CreateNewOrder />
            </ProtectedRoute>
          } />

          <Route path="/order/current-order" element={
            <ProtectedRoute> 
              <CurrentOrder />
            </ProtectedRoute>
          } />

          <Route path="/order/:orderId" element={
            <ProtectedRoute> 
              <OrderDetail />
            </ProtectedRoute>
          } />

          <Route path="/order/finished-order/:orderId" element={
            <ProtectedRoute> 
              <OrderDetailFinished />
            </ProtectedRoute>
          } />

          <Route path="/order/do-not-proceed/:orderId" element={
            <ProtectedRoute> 
              <OrderDetailDNP />
            </ProtectedRoute>
          } />

          <Route path="/order/finished-order" element={
            <ProtectedRoute>
              <FinishedOrder />
            </ProtectedRoute>
          } />

          <Route path="/order/do-not-proceed" element={
            <ProtectedRoute>
              <DoNotProceed />
            </ProtectedRoute>
          } />

          <Route path="/order/recently-deleted" element={
            <ProtectedRoute>
              <RecentlyDeleted />
            </ProtectedRoute>
          } />

           <Route path="/order/recently-deleted/:orderId" element={
            <ProtectedRoute>
              <OrderDetailDeleted />
            </ProtectedRoute>
          } />

          <Route path="/tools/quote-calculator" element={
            <ProtectedRoute>
              <QuoteCalculator />
            </ProtectedRoute>
          } />

          {/* Organization Settings */}

          <Route path="/organization/settings" element={
            <ProtectedRoute>
              <OrganizationSettings />
            </ProtectedRoute>
          } />

          <Route path="/organization/settings/create" element={
            <ProtectedRoute>
              <CreateOrganization />
            </ProtectedRoute>
          } />

          <Route path="/organization/settings/org-settings/:orgId" element={
            <ProtectedRoute>
              <OrgSettings />
            </ProtectedRoute>
          } />

          <Route path="/organization/settings/create-job-flow/:orgId" element={
            <ProtectedRoute>
              <CreateJobFlow />
            </ProtectedRoute>
          } />

          <Route path="/organization/settings/job-flow-settings/:orgId" element={
            <ProtectedRoute>
              <JobFlowSettings />
            </ProtectedRoute>
          } />

          <Route path="/organization/settings/job-flow-settings/:orgId/edit/:jobFlowId" element={
            <ProtectedRoute>
              <EditJobFlowSettings />
            </ProtectedRoute>
          } />

          {/* Organization Rules */}

          <Route path="/organization/roles" element={
            <ProtectedRoute>
              <OrganizationRoles />
            </ProtectedRoute>
          } />

          <Route path="/organization/roles/edit/:roleId" element={
            <ProtectedRoute>
              <EditOrganizationRoles />
            </ProtectedRoute>
          } />

          <Route path="/organization/roles/create" element={
            <ProtectedRoute>
              <CreateOrganizationRoles />
            </ProtectedRoute>
          } />

          <Route path="/organization/members" element={
            <ProtectedRoute>
              <OrganizationMembers />
            </ProtectedRoute>
          } />

          <Route path="/organization/members/create" element={
            <ProtectedRoute>
              <CreateOrganizationMember />
            </ProtectedRoute>
          } />

          <Route path="/organization/members/edit/:memberId" element={
            <ProtectedRoute>
              <EditOrganizationMember />
            </ProtectedRoute>
          } />

          <Route path="/organization/product-management" element={
            <ProtectedRoute>
              <ProductManagement />
            </ProtectedRoute>
          } />

          <Route path="/organization/customer-management" element={
            <ProtectedRoute>
              <CustomerManagement />
            </ProtectedRoute>
          } />

          <Route path="/organization/customer-management/create" element={
            <ProtectedRoute>
              <CreateCustomer />
            </ProtectedRoute>
          } />

          <Route path="/organization/customer-management/edit-customer/:customerId" element={
            <ProtectedRoute>
              <EditCustomer />
            </ProtectedRoute>
          } />

          <Route path="/organization/customer-management/edit-company/:companyId" element={
            <ProtectedRoute>
              <EditCompany />
            </ProtectedRoute>
          } />

          <Route path="/chat" element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          } />

          <Route path="settings/system-settings" element={
            <ProtectedRoute>
              <SystemSettings />
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />

          {/* Unauthorized Access Route */}
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Not Found Route */}
          <Route path="*" element={<NotFound />} />
          
          {/* Admin Routes */}
          <Route path="/admin/signin" element={<AdminSignin />} />
          <Route path="/admin/dashboard" element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } />
          
          <Route path="/admin/admins" element={
            <AdminRoute>
              <AdminAdmins />
            </AdminRoute>
          } />

          <Route path="/admin/organizations" element={
            <AdminRoute>
              <AdminOrganizations />
            </AdminRoute>
          } />

          <Route path="/admin/members" element={
            <AdminRoute>
              <AdminMembers />
            </AdminRoute>
          } />

          <Route path="/admin/sessions" element={
            <AdminRoute>
              <AdminSessions />
            </AdminRoute>
          } />

          <Route path="/admin/events" element={
            <AdminRoute>
              <AdminEvents />
            </AdminRoute>
          } />

          <Route path="/admin/settings" element={
            <AdminRoute>
              <AdminSettings />
            </AdminRoute>
          } />

          <Route path="/admin/organizations/:org_id" element={
            <AdminRoute>
              <AdminOrganizationDetails />
            </AdminRoute>
          } />




















        </Routes>
      </Router>
    </WebSocketProvider>
  );
};

export default App;
